import type { BondFlow } from "@/lib/brokerBonds";
import { isFiledStatus, submissionStatusLabel } from "@/lib/bondStateModel";

// The three presentational rules every list page in the portal repeats: how a
// timestamp reads in a table cell, and how a status maps onto a badge's palette
// and label. Extracted when the bond-order list (BRK-S088) would have been the
// THIRD copy — a repeated label-map is a smell (CLAUDE.md, Coding Standards).
//
// These are aesthetic mappings only; nothing here decides behaviour, and an
// unknown status degrades to a neutral chip rather than disappearing.

/** A created/updated timestamp as a plain `YYYY-MM-DD` — stable and
 *  locale-neutral, which is what a sortable table column wants. */
export function formatListDate(iso: string): string {
  return iso.slice(0, 10);
}

/** A timestamp as `m/d/yyyy` (D-10), for a list's Last modified / Date created
 *  column (BRK-S211). A timestamp is an instant, so it reads in the viewer's
 *  own calendar; a missing or unreadable value is a dash, never a guess. */
export function formatShortDate(iso: string | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-US");
}

/** A timestamp as `m/d/yyyy h:mm AM/PM` (D-10 plus the time), for a list
 *  column where the hour matters, such as a notice's Received (BRK-S252). */
export function formatShortDateTime(iso: string | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date.toLocaleDateString("en-US")} ${time}`;
}

/**
 * A history row's timestamp, the October wireframe's way: "10/14/2026 ·
 * 9:19:05 AM MT" — m/d/yyyy (D-10), the time to the second and the zone's
 * short generic name, in the viewer's own zone (`timeZone` pins one for a
 * test). "—" for a value that is not a date.
 */
export function formatHistoryStamp(
  iso: string | undefined,
  timeZone?: string,
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.toLocaleDateString("en-US", { timeZone });
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "shortGeneric",
    timeZone,
  });
  return `${day} · ${time}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/** When a notice arrived, as a glance reads it (the bell's panel, BRK-S252):
 *  "Just now", "12 minutes ago", "3 hours ago" today; "Yesterday"; then the
 *  date ("Sep 24"), with the year once it isn't this year. A clock a little
 *  ahead of PMS's still reads "Just now", never "in 2 minutes". */
export function formatNoticeTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const ago = now.getTime() - date.getTime();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay || ago < MINUTE) {
    if (ago < MINUTE) return "Just now";
    if (ago < HOUR) {
      const minutes = Math.floor(ago / MINUTE);
      return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    }
    const hours = Math.floor(ago / HOUR);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** The "this finished well" chip, shared by the statuses that mean it. */
const DONE_BADGE =
  "bg-green-100 dark:bg-green-400/15 text-green-700 dark:text-green-300";

/** Status → badge palette. Unknown statuses fall back to a neutral grey chip. */
export function statusBadgeClass(status: string): string {
  switch (status.toLowerCase()) {
    // A bond request's DECISION shares this palette rather than owning a second
    // one (BRK-S174): `approved` is the same "this finished well" chip as a
    // bound policy, `referred` already maps to the amber it should, and
    // `pending` falls through to the neutral default — which is correct without
    // a case of its own, because a decision nobody has made yet is not a state
    // to colour.
    // `done` is an action item Signal finished (PMS-S870, BRK-S465).
    case "approved":
    case "bound":
    case "active":
    case "done":
      return DONE_BADGE;
    case "quoted":
    case "in_progress":
    case "submitted":
      return "bg-blue-100 dark:bg-blue-400/15 text-blue-700 dark:text-blue-300";
    case "declined":
    case "error":
      return "bg-brk-accent/10 text-brk-accent";
    // An action item waiting on the broker wants their eye (BRK-S465).
    case "referred":
    case "waiting_on_carrier":
    case "waiting_on_broker":
      return "bg-amber-100 dark:bg-amber-400/15 text-amber-700 dark:text-amber-300";
    case "expired":
    case "cancelled":
    case "draft":
    default:
      return "bg-brk-bg text-brk-dim";
  }
}

/**
 * A `snake_case` key as Title Case (`bond_indemnity` → "Bond Indemnity").
 *
 * The last-resort label for a STATUS this bundle has never seen. Extracted from
 * `statusLabel` so the two humanising styles live side by side and their
 * difference is visible — see `sentenceCaseKey` below, which is the one a
 * document type's label falls back to, and why.
 */
export function titleCaseKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A key as a SENTENCE (`surety_rider_addendum` → "Surety rider addendum",
 * `bidDocuments` → "Bid documents").
 *
 * The other humanising style, and the difference from `titleCaseKey` is not
 * cosmetic: this one is the fallback for a LABEL, and PMS's document registry
 * writes labels as sentences ("Bank letter", "Prior bond form"), so a Title
 * Case fallback would print a label that reads unlike every one beside it.
 * `titleCaseKey` stays the style for a status BADGE, where every other chip is
 * Title Case.
 *
 * Handles `_`/`-` AND camelCase, because a document type key is snake_case and
 * a configuration field key is camelCase, and one regex for both would print
 * "Biddocuments".
 */
export function sentenceCaseKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (spaced === "") return key;
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Status → its human label (`waiting_on_carrier` → "Waiting on Carrier"). */
export function statusLabel(status: string): string {
  if (status === "waiting_on_carrier") return "Waiting on Carrier";
  return titleCaseKey(status);
}

// ── A bond order's status reads differently per FLOW (BRK-S160) ──────────────
//
// `submitted` is one value on the wire and two facts on screen. A bond request
// that is `submitted` has been **Filed with Signal** — somebody is going to
// underwrite it and the broker is waiting. A standalone pre-approval that is
// `submitted` is **Complete**: the decision has landed, it lives on the
// insured's Surety program block, and there is nothing left to wait for. Saying
// "Submitted" for the second would leave a broker watching a list for an answer
// they already have.
//
// Both halves live here rather than at each surface, so the label and the chip
// colour cannot disagree — three surfaces render this row.

/** Is this a finished standalone pre-approval, rather than a filed request?
 *  Any v2 filed status reads as `submitted` did (BRK-S432); a withdrawn or
 *  declined one keeps its own label. */
function isCompletedPreapproval(status: string, flow: BondFlow): boolean {
  return flow === "preapproval" && isFiledStatus(status);
}

/** A bond order's status label, in the words its FLOW makes true — the
 *  same closed label table as the submission's History rows (BRK-S502,
 *  `submissionStatusLabel`: "In review", never the wire's underwriting
 *  word; an unadopted status reads "In progress"). */
export function bondStatusLabel(
  status: string,
  flow: BondFlow = "bond",
): string {
  return isCompletedPreapproval(status, flow)
    ? "Complete"
    : submissionStatusLabel(status);
}

/** The chip for that label — green for a completed pre-approval, because it is
 *  an ending rather than a hand-off. */
export function bondStatusBadgeClass(
  status: string,
  flow: BondFlow = "bond",
): string {
  return isCompletedPreapproval(status, flow)
    ? DONE_BADGE
    : statusBadgeClass(status);
}
